import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import jwtDecode from "jwt-decode";
import { apiClient } from "../../api/client";

const AuthContext = createContext(null);

/**
 * AuthProvider — single source of truth for the authenticated user.
 *
 * Token storage strategy:
 *   - Access token: React ref (in-memory only — never written to localStorage).
 *   - Refresh token: HttpOnly cookie managed by the browser (set by the backend, TASK-02).
 *                   For M0 compatibility, falls back to a regular cookie via getCookie().
 *
 * Axios interceptors registered here:
 *   1. Request: attach Authorization header from the in-memory ref.
 *   2. Response: on 401, attempt silent refresh then retry the original request.
 *
 * Exposes (via useAuth()):
 *   { user, isAuthenticated, role, isLoading, login, logout,
 *     impersonation, startImpersonation, endImpersonation }
 */
function AuthProvider({ children }) {
  const accessTokenRef = useRef(null);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // Vista activa cuando el usuario tiene varios roles (rol dual). Se persiste
  // para que el switcher recuerde la elección entre recargas.
  const [activeRole, setActiveRoleState] = useState(
    () => localStorage.getItem("activeRole") || null
  );

  // ── Impersonación (superadmin "ver como" el owner de una academia) ────────
  // `null` = sesión normal. Con valor = estamos viendo la app como el owner
  // cuyo nombre/academia trae este objeto, pero la cookie de refresh sigue
  // siendo la del staff real (nunca se rota durante impersonación — ver
  // startImpersonation). `impersonationRef` espeja el estado para que el
  // interceptor de abajo (que no debe re-suscribirse en cada cambio) siempre
  // lea el valor vigente.
  const [impersonation, setImpersonation] = useState(null);
  const impersonationRef = useRef(null);
  useEffect(() => {
    impersonationRef.current = impersonation;
  }, [impersonation]);

  const setActiveRole = useCallback((role) => {
    setActiveRoleState(role);
    if (role) localStorage.setItem("activeRole", role);
  }, []);

  // Añade un rol recién otorgado (p. ej. el dueño se activa como docente) sin
  // esperar a re-emitir el JWT: el backend ya autoriza por membresía en DB.
  const addRole = useCallback((role) => {
    setUser((prev) =>
      prev
        ? { ...prev, roles: [...new Set([...(prev.roles ?? []), role])] }
        : prev
    );
  }, []);

  // ── Silent refresh ────────────────────────────────────────────────────────
  const refreshAccessToken = useCallback(async () => {
    // The refresh token is an HttpOnly cookie set by the backend; the browser
    // sends it automatically when withCredentials is on. The backend also reads
    // it from the cookie and rotates it (Set-Cookie on the response), so JS never
    // touches the refresh token. Body stays empty for browser clients.
    const { default: axios } = await import("axios");
    const response = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL || ""}/api/v1/auth/jwt/refresh/`,
      {},
      { withCredentials: true }
    );
    return response.data.access;
  }, []);

  // ── Token → user (compartido por hydrate() y endImpersonation()) ──────────
  // Dado un access token ya válido, reconstruye el `user` del contexto desde
  // /users/me/ + claims del JWT. Deja `accessTokenRef` apuntando a ese token
  // (así el interceptor de request ya lo usa para esta misma llamada).
  const applyTokenAsSession = useCallback(async (accessToken) => {
    accessTokenRef.current = accessToken;
    const response = await apiClient.get("/api/v1/users/me/");
    const userData = response.data;

    // Enrich with role from JWT claims (docs/05 §3) so RequireRole can gate without
    // an extra request.  The backend claim is the authoritative UI hint; actual
    // authorization is always enforced server-side.
    // Tenant context (slug/company_id) is NOT in /users/me — it lives in the
    // JWT claims. Extract it here so a page reload keeps the slug-scoped nav
    // and redirects working.
    // /users/me es AUTORITATIVO para role/roles (los deriva de la DB), así
    // que un rol recién otorgado se refleja sin re-emitir el JWT. El token
    // solo aporta el contexto de tenant (slug/company_id) y sirve de fallback.
    let role = userData.role ?? null;
    let roles = userData.roles ?? null;
    let slug = null;
    let companyId = null;
    let id = userData.id ?? null;
    let mustChangePassword = userData.must_change_password ?? false;
    try {
      const claims = jwtDecode(accessToken);
      if (role == null) role = claims.role ?? null;
      if (roles == null) roles = claims.roles ?? null;
      slug = claims.slug ?? null;
      companyId = claims.company_id ?? null;
      // /users/me/ nunca trae `id` (evita exponer el pk innecesariamente) — el
      // claim estándar de SimpleJWT (USER_ID_CLAIM) es la única fuente. Sirve
      // para que el SPA se reconozca a sí mismo en listas de otro endpoint
      // (p. ej. preseleccionar "yo" como instructor al crear una clase).
      if (id == null) id = claims.user_id ?? null;
      if (claims.must_change_password != null) {
        mustChangePassword = claims.must_change_password;
      }
    } catch {
      // jwt-decode failed (malformed token) — fall back to /users/me payload
    }

    if (!roles || roles.length === 0) roles = role ? [role] : [];

    setUser({
      ...userData,
      id,
      role,
      roles,
      slug,
      company_id: companyId,
      must_change_password: mustChangePassword,
      student_frozen: userData.student_frozen ?? false,
      // TASK-20: roles internos de staff de plataforma
      // (PLATFORM_ADMIN/FINANCE/SUPPORT/SALES/READ_ONLY). `[]` = no es staff
      // o no tiene grupo asignado. Solo insumo de UX: el panel super-admin
      // oculta pestañas/botones con ellos, pero el backend revalida cada
      // petición (features/superadmin/permissions.js).
      staff_roles: Array.isArray(userData.staff_roles)
        ? userData.staff_roles
        : [],
    });
  }, []);

  // ── Axios interceptors ────────────────────────────────────────────────────
  useEffect(() => {
    const requestId = apiClient.interceptors.request.use((config) => {
      if (accessTokenRef.current) {
        config.headers.Authorization = `Bearer ${accessTokenRef.current}`;
      }
      return config;
    });

    const responseId = apiClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error.config;
        if (error.response?.status === 401 && !original._retry) {
          original._retry = true;
          try {
            const newToken = await refreshAccessToken();
            // El refresh SIEMPRE usa la cookie del staff real (la
            // impersonación nunca emite/rota una propia — ver
            // startImpersonation), así que si el access token de
            // impersonación vence a mitad de sesión, este refresh
            // silenciosamente revierte a la identidad real. Si estábamos
            // impersonando, hay que reflejarlo: recargar `user` desde el
            // nuevo token (que ya NO es del owner) y apagar el banner —
            // más caro que el simple `accessTokenRef.current = newToken`
            // de siempre, pero solo corre en este caso, no en cada 401.
            if (impersonationRef.current) {
              await applyTokenAsSession(newToken);
              setImpersonation(null);
            } else {
              accessTokenRef.current = newToken;
            }
            original.headers.Authorization = `Bearer ${newToken}`;
            return apiClient(original);
          } catch {
            // Refresh failed — clear session; redirect handled by RequireRole
            accessTokenRef.current = null;
            setUser(null);
            setImpersonation(null);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      apiClient.interceptors.request.eject(requestId);
      apiClient.interceptors.response.eject(responseId);
    };
  }, [refreshAccessToken, applyTokenAsSession]);

  // ── Initial session hydration ─────────────────────────────────────────────
  useEffect(() => {
    async function hydrate() {
      // M0 bridge: seed from localStorage if the old interceptor stored a token there.
      // Will be removed once the backend issues tokens via HttpOnly cookie (TASK-02/05).
      const legacy = localStorage.getItem("access_token");
      if (legacy) {
        accessTokenRef.current = legacy;
      }

      if (!accessTokenRef.current) {
        // No token in memory — attempt a silent refresh via the cookie
        try {
          accessTokenRef.current = await refreshAccessToken();
        } catch {
          setIsLoading(false);
          return;
        }
      }

      try {
        await applyTokenAsSession(accessTokenRef.current);
      } catch {
        accessTokenRef.current = null;
      } finally {
        setIsLoading(false);
      }
    }

    hydrate();
  }, [refreshAccessToken, applyTokenAsSession]);

  // ── Public API ────────────────────────────────────────────────────────────
  const login = useCallback((accessToken, userData) => {
    accessTokenRef.current = accessToken;
    // The refresh token is set as an HttpOnly cookie by the backend (jwt/create);
    // JS never sees it. We only keep the access token in memory.
    let role = userData?.role ?? null;
    let roles = userData?.roles ?? (role ? [role] : []);
    let name = userData?.name ?? null;
    let email = userData?.email ?? null;
    let slug = userData?.slug ?? null;
    let companyId = userData?.company_id ?? null;
    let id = userData?.id ?? null;
    let mustChangePassword = userData?.must_change_password ?? false;
    try {
      const claims = jwtDecode(accessToken);
      role = claims.role ?? role;
      roles = claims.roles ?? roles;
      name = claims.name || name;
      email = claims.email || email;
      slug = claims.slug || slug;
      companyId = claims.company_id || companyId;
      id = claims.user_id ?? id;
      if (claims.must_change_password != null) {
        mustChangePassword = claims.must_change_password;
      }
    } catch {
      /* ignore */
    }
    // Tokens viejos no traen el claim `roles`; deriva del rol primario.
    if (!roles || roles.length === 0) roles = role ? [role] : [];
    let studentFrozen = userData?.student_frozen ?? false;
    try {
      const claims = jwtDecode(accessToken);
      if (claims.student_frozen != null) studentFrozen = claims.student_frozen;
    } catch {
      /* ignore */
    }
    setUser({
      ...userData,
      id,
      role,
      roles,
      name,
      email,
      slug,
      company_id: companyId,
      must_change_password: mustChangePassword,
      student_frozen: studentFrozen,
      // El payload de login son los claims del JWT, que NO incluyen `is_staff`
      // ni `staff_roles`. Se deja `undefined` a propósito = "desconocido" (ver
      // resolveStaffRoles) y lo completa el efecto de abajo con /users/me/.
      staff_roles: userData?.staff_roles ?? undefined,
    });
    // Do NOT store the access token in localStorage (XSS can't steal it)
  }, []);

  // ── Completar el contexto de staff tras un login ──────────────────────────
  // /users/me/ es autoritativo para is_staff/staff_roles y el JWT no los emite,
  // así que una sesión nacida de login() (sin recargar) no los conoce y el panel
  // super-admin no podría gatear por rol. Una sola petición los completa.
  // Idempotente: hydrate() ya deja un array, así que en la ruta de F5 no corre.
  useEffect(() => {
    if (!user || user.staff_roles !== undefined) return;
    let cancelled = false;
    apiClient
      .get("/api/v1/users/me/")
      .then(({ data }) => {
        if (cancelled) return;
        setUser((prev) =>
          prev
            ? {
                ...prev,
                id: prev.id ?? data.id,
                is_staff: data.is_staff ?? prev.is_staff ?? false,
                is_superuser: data.is_superuser ?? prev.is_superuser ?? false,
                staff_roles: Array.isArray(data.staff_roles)
                  ? data.staff_roles
                  : [],
              }
            : prev
        );
      })
      .catch(() => {
        // Sin respuesta → marca "sin roles conocidos" para no reintentar en bucle.
        if (!cancelled) {
          setUser((prev) => (prev ? { ...prev, staff_roles: [] } : prev));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Clear the forced-password-change flag after a successful rotation so the
  // ChangePasswordGate releases the user into the app without a full reload.
  const markPasswordChanged = useCallback(() => {
    setUser((prev) => (prev ? { ...prev, must_change_password: false } : prev));
  }, []);

  // Merge fields into the current user (p. ej. edición inline del nombre en
  // Perfil) para que el cambio se refleje al instante sin recargar la sesión.
  const updateUser = useCallback((patch) => {
    setUser((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const logout = useCallback(async () => {
    try {
      // Ask the backend to blacklist the refresh token (TASK-02 implements the endpoint)
      await apiClient.post("/api/v1/auth/jwt/logout/");
    } catch {
      // Best-effort; still clear local state even if the request fails
    } finally {
      // The HttpOnly refresh cookie is cleared by the backend logout response.
      accessTokenRef.current = null;
      localStorage.removeItem("access_token"); // clean up M0 legacy storage
      localStorage.removeItem("activeRole"); // reset dual-role view for next user
      setUser(null);
      setImpersonation(null);
    }
  }, []);

  // ── Impersonación ──────────────────────────────────────────────────────────
  // startImpersonation: cambia la sesión activa al owner de una academia, con
  // el access token que ya emitió el backend (superadmin/companies/{id}/
  // impersonate/). A propósito NO toca la cookie de refresh — sigue siendo la
  // del staff real, sin rotar — así endImpersonation puede volver sin login.
  const startImpersonation = useCallback(
    async (accessToken, company) => {
      await applyTokenAsSession(accessToken);
      setImpersonation({ company });
    },
    [applyTokenAsSession]
  );

  // endImpersonation: recupera la sesión real del staff refrescando con la
  // cookie de refresh original (nunca tocada mientras se impersonaba).
  const endImpersonation = useCallback(async () => {
    const token = await refreshAccessToken();
    await applyTokenAsSession(token);
    setImpersonation(null);
  }, [refreshAccessToken, applyTokenAsSession]);

  // Rol de la vista activa: la elección persistida del switcher si sigue siendo
  // válida; si no, el rol primario. `role` se mantiene = vista activa para que el
  // resto de la app (nav, guards) reaccione al switcher sin cambios.
  const roles = user?.roles ?? [];
  const effectiveActiveRole =
    activeRole && roles.includes(activeRole) ? activeRole : user?.role ?? null;

  const value = {
    user,
    isAuthenticated: Boolean(user),
    role: effectiveActiveRole,
    primaryRole: user?.role ?? null,
    roles,
    activeRole: effectiveActiveRole,
    setActiveRole,
    addRole,
    studentFrozen: user?.student_frozen ?? false,
    // Roles de staff de plataforma (TASK-20), solo lectura. `null` = aún
    // desconocido. Consúmelos vía useStaffPermissions, no a pelo.
    staffRoles: user?.staff_roles ?? null,
    mustChangePassword: user?.must_change_password ?? false,
    isLoading,
    login,
    logout,
    // Impersonación (superadmin, PLATFORM_ADMIN): `impersonation` es `null` en
    // sesión normal, `{ company }` mientras se ve la app como el owner.
    impersonation,
    startImpersonation,
    endImpersonation,
    markPasswordChanged,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}

export { AuthProvider, useAuth, AuthContext };
